
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { CountrySelector } from './visa/CountrySelector';
import { VisaRequirementsList } from './visa/VisaRequirementsList';
import { useVisaData } from '@/hooks/useVisaData';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';

const VisaRecommendations = () => {
  const [selectedCountry, setSelectedCountry] = React.useState<string | null>(null);

  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Session query error:', error);
      }
      return session;
    },
  });

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['profile', session?.user?.id],
    enabled: !!session?.user?.id,
    queryFn: async () => {
      console.log('Fetching profile for user:', session?.user?.id);
      const { data, error } = await supabase
        .from('profiles')
        .select('nationality')
        .eq('id', session.user.id)
        .single();
      
      if (error) {
        console.error('Profile fetch error:', error);
        throw error;
      }
      console.log('Fetched profile data:', data);
      return data;
    },
  });

  const {
    originCountries,
    visaRequirements,
    isLoadingCountries,
    isLoadingVisa,
  } = useVisaData(selectedCountry);

  React.useEffect(() => {
    if (profile?.nationality && !selectedCountry) {
      console.log('Setting selected country to profile nationality:', profile.nationality);
      setSelectedCountry(profile.nationality);
    }
  }, [profile, selectedCountry]);

  if (isLoadingCountries || isLoadingProfile) {
    return (
      <div className="text-center py-8">Loading countries data...</div>
    );
  }

  if (!session) {
    return (
      <div className="text-center">
        <Alert>
          <AlertDescription className="text-base">
            Please <Link to="/auth" className="text-primary font-medium hover:underline">sign in</Link> to view visa requirements
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="w-full bg-white shadow-lg">
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="text-gray-600">
              Discover where you can travel based on your passport
            </div>
            
            <div className="space-y-4">
              <CountrySelector
                countries={originCountries}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
              />

              {isLoadingVisa ? (
                <div className="text-center py-8">Loading visa requirements...</div>
              ) : selectedCountry && visaRequirements?.length === 0 ? (
                <div className="text-center py-8">
                  No visa requirements found for the selected country. Please try selecting a different country.
                </div>
              ) : selectedCountry && visaRequirements ? (
                <VisaRequirementsList requirements={visaRequirements} />
              ) : (
                <div className="text-center py-8 text-gray-600">
                  Please select your country to view visa requirements
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VisaRecommendations;
